import { useState, useCallback } from 'react';
import { 
  getCategories, 
  getCategoryById, 
  createCategory, 
  updateCategory, 
  deleteCategory 
} from '../services/homeExpenseGroupCategoriesApi';
import { 
  HomeExpenseGroupCategoryDto, 
  CreateHomeExpenseGroupCategoryCommand, 
  UpdateHomeExpenseGroupCategoryCommand 
} from '../types/homeExpenses';

export const useCategories = () => {
  const [categories, setCategories] = useState<HomeExpenseGroupCategoryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCategories();
      setCategories(result);
    } catch (err: any) {
      setError(err.message || 'Não foi possível carregar as categorias');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategoryById = useCallback(async (publicId: string): Promise<HomeExpenseGroupCategoryDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCategoryById(publicId);
      return result;
    } catch (err: any) {
      setError(err.message || 'Não foi possível carregar a categoria');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createNewCategory = useCallback(async (category: CreateHomeExpenseGroupCategoryCommand): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const newCategory = await createCategory(category);
      setCategories(prev => [...prev, newCategory].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)));
      return true;
    } catch (err: any) {
      setError(err.message || 'Não foi possível criar a categoria');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateExistingCategory = useCallback(async (publicId: string, category: UpdateHomeExpenseGroupCategoryCommand): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const updatedCategory = await updateCategory(publicId, category);
      setCategories(prev => 
        prev.map(cat => cat.publicId === publicId ? updatedCategory : cat)
           .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      );
      return true;
    } catch (err: any) {
      setError(err.message || 'Não foi possível atualizar a categoria');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteExistingCategory = useCallback(async (publicId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await deleteCategory(publicId);
      setCategories(prev => prev.filter(cat => cat.publicId !== publicId));
      return true;
    } catch (err: any) {
      setError(err.message || 'Não foi possível eliminar a categoria');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    categories,
    loading,
    error,
    fetchCategories,
    fetchCategoryById,
    createNewCategory,
    updateExistingCategory,
    deleteExistingCategory,
  };
};
